import { ref } from 'vue'

import { addressSearchPath, addressSearchSelectPath } from '@/routes'
import type { AddressFields, AddressSearchResult } from '@/types'

export function useAddressSearch() {
  const results = ref<AddressSearchResult[]>([])
  const loading = ref(false)
  let debounce: ReturnType<typeof setTimeout> | null = null

  async function search(query: string) {
    if (debounce) clearTimeout(debounce)
    if (!query || query.trim().length < 3) {
      results.value = []
      return
    }
    debounce = setTimeout(async () => {
      loading.value = true
      try {
        const res = await fetch(`${addressSearchPath()}?q=${encodeURIComponent(query)}`)
        results.value = await res.json()
      } catch {
        results.value = []
      } finally {
        loading.value = false
      }
    }, 300)
  }

  async function refine(searchResultId: string) {
    loading.value = true
    try {
      const res = await fetch(`${addressSearchPath()}?id=${encodeURIComponent(searchResultId)}`)
      results.value = await res.json()
    } catch {
      results.value = []
    } finally {
      loading.value = false
    }
  }

  async function selectAddress(searchResultId: string): Promise<Partial<AddressFields>> {
    try {
      const res = await fetch(
        `${addressSearchSelectPath()}?id=${encodeURIComponent(searchResultId)}`,
      )
      return await res.json()
    } catch {
      return {}
    }
  }

  function clearResults() {
    if (debounce) clearTimeout(debounce)
    results.value = []
  }

  return { results, loading, search, refine, selectAddress, clearResults }
}
